// screens/CouponsAdminScreen.jsx
//
// HADACY · admin · cupones
//
// On-brand redesign of the coupons admin. The previous version used a dense
// react-bootstrap table with cryptic column headers (NAME / TYPE / REGLA /
// VIGENTE AHORA / ACUMULABLE...) and relied on the admin remembering what
// each toggle did. This rewrite swaps that for the .hadacy-coupons design
// system shared with the rest of the admin: cream card surfaces, gold
// hairlines, serif headings, status pills, and inline plain-Spanish help
// next to each control. We also moved from a giant single form into three
// labeled sections (Identidad → Regla → Vigencia & uso) so the create/edit
// flow reads top-to-bottom.
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import {
  fetchCoupons,
  upsertCoupon,
  deleteCoupon,
} from "../actions/couponsActions";

const TYPES = [
  { value: "PERCENT", label: "Porcentaje (%)" },
  {
    value: "BUY_X_GET_Y_SAME_TIPO",
    label: "Compra X y llévate Y gratis (mismo tipo)",
  },
];

const TIPO_OPTIONS = [
  { value: "*", label: "Cualquiera (perfumes y cosméticos)" },
  { value: "perfumes", label: "Solo perfumes" },
  { value: "cosmeticos", label: "Solo cosméticos" },
];

const defaultForm = {
  _id: null,
  Name: "",
  Description: "",
  Active: false,
  Type: "PERCENT",
  Discount: 10,
  ThresholdQty: 4,
  FreeQty: 1,
  // The backend only supports matching by `tipo` for BUY_X_GET_Y today,
  // so we pin these here and never expose them in the UI.
  MustMatchSameField: true,
  MatchField: "tipo",
  AppliesToTipo: "*",
  StartsAt: "",
  EndsAt: "",
  Stackable: false,
};

// ─── Status helpers ─────────────────────────────────────────────────────
// A coupon's "live" state is a function of (Active toggle) ∧ (now within
// StartsAt/EndsAt). We surface four states so the admin can tell at a
// glance what's happening without clicking in:
//   Activo      — Active=true and within window
//   Programado  — Active=true but StartsAt is in the future
//   Expirado    — EndsAt is in the past (regardless of Active)
//   Pausado     — Active=false (and not expired)
const couponStatus = (c) => {
  const now = Date.now();
  const s = c?.StartsAt ? Date.parse(c.StartsAt) : null;
  const e = c?.EndsAt ? Date.parse(c.EndsAt) : null;
  if (e != null && now > e) return "expired";
  if (!c?.Active) return "paused";
  if (s != null && now < s) return "scheduled";
  return "active";
};

const STATUS_META = {
  active: { label: "Activo", tone: "ok" },
  scheduled: { label: "Programado", tone: "info" },
  paused: { label: "Pausado", tone: "muted" },
  expired: { label: "Expirado", tone: "warn" },
};

const fmtDT = (v) =>
  v
    ? new Date(v).toLocaleString("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const CouponsAdminScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((s) => s.userLogin);
  const { loading, error, coupons } = useSelector((s) => s.couponList || {});

  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [toast, setToast] = useState({ show: false, message: "", tone: "ok" });
  const showToast = (message, tone = "ok") =>
    setToast({ show: true, message, tone });

  // Auto-dismiss the toast after 3.2s — same cadence as react-bootstrap's.
  useEffect(() => {
    if (!toast.show) return undefined;
    const t = setTimeout(
      () => setToast((prev) => ({ ...prev, show: false })),
      3200
    );
    return () => clearTimeout(t);
  }, [toast.show]);

  useEffect(() => {
    if (!userInfo || !userInfo.isAdmin) {
      navigate("/login");
      return;
    }
    dispatch(fetchCoupons());
  }, [dispatch, navigate, userInfo]);

  const list = useMemo(() => {
    const f = (filter || "").toLowerCase().trim();
    let result = coupons || [];
    if (f) {
      result = result.filter(
        (c) =>
          c.Name?.toLowerCase().includes(f) ||
          c.Description?.toLowerCase().includes(f) ||
          c.Type?.toLowerCase().includes(f)
      );
    }
    if (statusFilter !== "all") {
      result = result.filter((c) => couponStatus(c) === statusFilter);
    }
    return result;
  }, [coupons, filter, statusFilter]);

  // Counts for the tab pills above the list — recomputed on every coupon
  // change so newly-saved coupons immediately move between counters.
  const statusCounts = useMemo(() => {
    const counts = { all: 0, active: 0, scheduled: 0, paused: 0, expired: 0 };
    (coupons || []).forEach((c) => {
      counts.all += 1;
      counts[couponStatus(c)] += 1;
    });
    return counts;
  }, [coupons]);

  // ─── Handlers ─────────────────────────────────────────────────────────
  const onEdit = (c) => {
    setForm({
      _id: c._id,
      Name: c.Name || "",
      Description: c.Description || "",
      Active: !!c.Active,
      Type: c.Type || "PERCENT",
      Discount: c.Discount ?? 0,
      ThresholdQty: c.ThresholdQty ?? 4,
      FreeQty: c.FreeQty ?? 1,
      MustMatchSameField: true,
      MatchField: "tipo",
      AppliesToTipo:
        c.AppliesToTipo &&
        ["*", "perfumes", "cosmeticos"].includes(
          (c.AppliesToTipo || "").toLowerCase()
        )
          ? (c.AppliesToTipo || "*").toLowerCase()
          : "*",
      StartsAt: c.StartsAt
        ? new Date(c.StartsAt).toISOString().slice(0, 16)
        : "",
      EndsAt: c.EndsAt ? new Date(c.EndsAt).toISOString().slice(0, 16) : "",
      Stackable: !!c.Stackable,
    });
    // Scroll the form into view so the admin sees they're now editing.
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const resetForm = () => setForm(defaultForm);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.Name.trim()) {
      showToast("Necesitas un código (nombre del cupón).", "warn");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        // Force these regardless of state — the type-switcher below also
        // pins them, but a defensive double-write here costs nothing.
        MustMatchSameField: true,
        MatchField: "tipo",
        StartsAt: form.StartsAt ? new Date(form.StartsAt).toISOString() : null,
        EndsAt: form.EndsAt ? new Date(form.EndsAt).toISOString() : null,
        AppliesToTipo: (form.AppliesToTipo || "*")?.toLowerCase(),
      };
      await dispatch(upsertCoupon(payload));
      await dispatch(fetchCoupons());
      showToast(
        form._id ? "Cupón actualizado." : "Cupón creado.",
        "ok"
      );
      setSaving(false);
      resetForm();
    } catch (err) {
      console.error(err);
      setSaving(false);
      showToast("No se pudo guardar el cupón.", "danger");
    }
  };

  const onDelete = async (c) => {
    if (
      !window.confirm(
        `¿Eliminar el cupón "${c.Name}"? Esta acción no se puede deshacer.`
      )
    )
      return;
    setDeletingIds((prev) => new Set(prev).add(c._id));
    try {
      await dispatch(deleteCoupon(c._id));
      await dispatch(fetchCoupons());
      showToast("Cupón eliminado.", "ok");
    } catch {
      showToast("No se pudo eliminar.", "danger");
    } finally {
      setDeletingIds((prev) => {
        const n = new Set(prev);
        n.delete(c._id);
        return n;
      });
    }
  };

  // Plain-Spanish summary used both inside the form (live preview) and on
  // each card in the list. Keeps the wording identical so admins read the
  // same rule whether they're reviewing or editing.
  const renderRuleSummary = (c) => {
    if (c.Type === "PERCENT") {
      return (
        <span>
          <strong>{c.Discount}%</strong> de descuento sobre el subtotal del
          carrito.
        </span>
      );
    }
    return (
      <span>
        Por cada <strong>{c.ThresholdQty}</strong>{" "}
        {c.AppliesToTipo === "perfumes"
          ? "perfumes"
          : c.AppliesToTipo === "cosmeticos"
          ? "cosméticos"
          : "productos del mismo tipo"}{" "}
        en el carrito, <strong>{c.FreeQty}</strong>{" "}
        {c.FreeQty === 1 ? "es gratis" : "son gratis"} (se aplica al/los más
        baratos del grupo).
      </span>
    );
  };

  const renderFormRulePreview = () => {
    const sample = {
      Type: form.Type,
      Discount: form.Discount,
      ThresholdQty: form.ThresholdQty,
      FreeQty: form.FreeQty,
      AppliesToTipo: form.AppliesToTipo,
    };
    return (
      <div className="hadacy-coupons-preview">
        <div className="hadacy-coupons-preview__label">Vista previa</div>
        <div className="hadacy-coupons-preview__body">
          {renderRuleSummary(sample)}
        </div>
      </div>
    );
  };

  return (
    <div className="hadacy-coupons">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600&family=Jost:wght@200;300;400;500&display=swap');

        /* ─── HADACY · admin coupons ─────────────────────────────────────
           Two-column layout on desktop (form left, list right) collapsing
           to a single column on mobile. Same cream/gold tokens as the rest
           of the admin so the screen visually belongs to the suite. */
        .hadacy-coupons {
          --cream: #f5f0e8;
          --dark: #1a1410;
          --gold: #b8933a;
          --gold-light: #d4af6a;
          --muted: #7a6e62;
          --card-bg: #faf7f2;
          --recess: #efe7d8;
          --hairline: rgba(184, 147, 58, 0.18);
          --danger: #b04a3c;
          --success: #5b7a3c;
          font-family: "Jost", sans-serif;
          color: var(--dark);
          max-width: 1240px;
          margin: 1.6rem auto 3rem;
          padding: 0 1.2rem;
        }

        /* Page header — small uppercase eyebrow, serif title, gold rule. */
        .hadacy-coupons__header {
          margin-bottom: 1.4rem;
        }
        .hadacy-coupons__eyebrow {
          font-size: 0.66rem;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.4rem;
        }
        .hadacy-coupons__title {
          font-family: "Cormorant Garamond", serif;
          font-weight: 400;
          font-size: 2.1rem;
          line-height: 1.15;
          color: var(--dark);
          margin: 0 0 0.4rem 0;
        }
        .hadacy-coupons__lede {
          font-size: 0.92rem;
          color: var(--muted);
          line-height: 1.55;
          max-width: 640px;
          margin: 0;
        }

        /* Help / explanation panel that lists the two coupon types in
           plain Spanish so the admin doesn't have to guess what each does. */
        .hadacy-coupons-help {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-radius: 14px;
          padding: 1.2rem 1.3rem;
          margin: 1.2rem 0 1.6rem;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.1rem;
        }
        @media (max-width: 720px) {
          .hadacy-coupons-help { grid-template-columns: 1fr; }
        }
        .hadacy-coupons-help__item {
          display: flex;
          gap: 0.85rem;
        }
        .hadacy-coupons-help__icon {
          flex: 0 0 auto;
          width: 36px; height: 36px;
          border-radius: 999px;
          background: rgba(184, 147, 58, 0.12);
          color: var(--gold);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.95rem;
        }
        .hadacy-coupons-help__title {
          font-family: "Cormorant Garamond", serif;
          font-size: 1.15rem;
          color: var(--dark);
          margin: 0 0 0.25rem 0;
          line-height: 1.2;
        }
        .hadacy-coupons-help__body {
          font-size: 0.84rem;
          color: var(--muted);
          line-height: 1.55;
          margin: 0;
        }
        .hadacy-coupons-help__body code {
          background: var(--recess);
          color: var(--dark);
          padding: 0.05rem 0.3rem;
          border-radius: 4px;
          font-size: 0.78rem;
        }

        /* Two-column shell. */
        .hadacy-coupons__grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
          gap: 1.4rem;
          align-items: start;
        }
        @media (max-width: 980px) {
          .hadacy-coupons__grid { grid-template-columns: 1fr; }
        }

        /* Generic panel — used by both the form and list cards. */
        .hadacy-coupons-panel {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-radius: 16px;
          padding: 1.5rem 1.5rem;
          box-shadow: 0 14px 40px rgba(26, 20, 16, 0.04);
        }
        .hadacy-coupons-panel__head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.8rem;
          padding-bottom: 0.9rem;
          margin-bottom: 1.1rem;
          border-bottom: 1px solid var(--hairline);
        }
        .hadacy-coupons-panel__title {
          font-family: "Cormorant Garamond", serif;
          font-size: 1.5rem;
          color: var(--dark);
          margin: 0;
          font-weight: 400;
        }
        .hadacy-coupons-panel__hint {
          font-size: 0.78rem;
          color: var(--muted);
          letter-spacing: 0.04em;
        }

        /* Form sections — three labeled groups so the create/edit flow
           reads as Identidad → Regla → Vigencia. */
        .hadacy-coupons-section { margin-bottom: 1.5rem; }
        .hadacy-coupons-section:last-of-type { margin-bottom: 0.5rem; }
        .hadacy-coupons-section__title {
          font-size: 0.62rem;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: var(--gold);
          margin: 0 0 0.85rem 0;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .hadacy-coupons-section__title::after {
          content: "";
          flex: 1;
          height: 1px;
          background: var(--hairline);
        }

        .hadacy-coupons-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.9rem;
        }
        .hadacy-coupons-row--3 { grid-template-columns: 1fr 1fr 1fr; }
        @media (max-width: 600px) {
          .hadacy-coupons-row,
          .hadacy-coupons-row--3 { grid-template-columns: 1fr; }
        }

        .hadacy-coupons-field { margin-bottom: 0.85rem; }
        .hadacy-coupons-field:last-child { margin-bottom: 0; }
        .hadacy-coupons-label {
          display: block;
          font-size: 0.62rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--muted);
          font-weight: 400;
          margin-bottom: 0.35rem;
        }
        .hadacy-coupons-input,
        .hadacy-coupons-select {
          width: 100%;
          background: #fff;
          border: 1px solid rgba(184, 147, 58, 0.22);
          border-radius: 10px;
          padding: 0.65rem 0.9rem;
          font-family: "Jost", sans-serif;
          font-size: 0.93rem;
          color: var(--dark);
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .hadacy-coupons-input:focus,
        .hadacy-coupons-select:focus {
          outline: none;
          border-color: var(--gold);
          box-shadow: 0 0 0 3px rgba(184, 147, 58, 0.14);
        }
        .hadacy-coupons-input::placeholder { color: var(--muted); font-style: italic; }
        .hadacy-coupons-help-text {
          display: block;
          font-size: 0.78rem;
          color: var(--muted);
          line-height: 1.5;
          margin-top: 0.35rem;
        }
        .hadacy-coupons-help-text code {
          background: var(--recess);
          color: var(--dark);
          padding: 0.05rem 0.3rem;
          border-radius: 4px;
          font-size: 0.74rem;
        }

        /* Toggle row — switch + label + description block stacked. */
        .hadacy-coupons-switch {
          display: flex;
          align-items: flex-start;
          gap: 0.85rem;
          padding: 0.85rem 0.95rem;
          background: var(--recess);
          border: 1px solid var(--hairline);
          border-radius: 12px;
        }
        .hadacy-coupons-switch__toggle {
          flex: 0 0 auto;
          width: 38px; height: 22px;
          background: #d8cdb8;
          border-radius: 999px;
          position: relative;
          cursor: pointer;
          transition: background .2s ease;
          margin-top: 2px;
        }
        .hadacy-coupons-switch__toggle::after {
          content: "";
          position: absolute;
          top: 2px; left: 2px;
          width: 18px; height: 18px;
          border-radius: 999px;
          background: #fff;
          transition: transform .2s ease;
          box-shadow: 0 1px 3px rgba(26, 20, 16, 0.18);
        }
        .hadacy-coupons-switch input { display: none; }
        .hadacy-coupons-switch input:checked + .hadacy-coupons-switch__toggle {
          background: var(--gold);
        }
        .hadacy-coupons-switch input:checked + .hadacy-coupons-switch__toggle::after {
          transform: translateX(16px);
        }
        .hadacy-coupons-switch__copy {
          flex: 1;
          min-width: 0;
        }
        .hadacy-coupons-switch__title {
          font-size: 0.88rem;
          color: var(--dark);
          font-weight: 500;
          margin: 0 0 0.15rem 0;
        }
        .hadacy-coupons-switch__desc {
          font-size: 0.78rem;
          color: var(--muted);
          line-height: 1.45;
          margin: 0;
        }

        .hadacy-coupons-preview {
          margin-top: 0.6rem;
          padding: 0.75rem 0.9rem;
          background: rgba(184, 147, 58, 0.08);
          border: 1px solid rgba(184, 147, 58, 0.28);
          border-radius: 10px;
        }
        .hadacy-coupons-preview__label {
          font-size: 0.6rem;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.3rem;
          font-weight: 500;
        }
        .hadacy-coupons-preview__body {
          font-size: 0.88rem;
          color: var(--dark);
          line-height: 1.5;
        }

        /* CTA buttons — pill primary + ghost secondary. Same as auth/orders. */
        .hadacy-coupons-actions {
          display: flex;
          gap: 0.65rem;
          margin-top: 1.2rem;
          padding-top: 1rem;
          border-top: 1px solid var(--hairline);
          flex-wrap: wrap;
        }
        .hadacy-coupons-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border-radius: 999px;
          background: var(--dark);
          border: 1px solid var(--dark);
          color: var(--gold-light);
          font-size: 0.7rem;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          cursor: pointer;
          font-family: "Jost", sans-serif;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .hadacy-coupons-cta:hover:not(:disabled) {
          background: var(--gold);
          border-color: var(--gold);
          color: var(--dark);
        }
        .hadacy-coupons-cta:disabled { opacity: 0.65; cursor: not-allowed; }
        .hadacy-coupons-cta--ghost {
          background: transparent;
          color: var(--muted);
          border: 1px solid var(--hairline);
        }
        .hadacy-coupons-cta--ghost:hover:not(:disabled) {
          background: var(--recess);
          color: var(--dark);
          border-color: var(--gold);
        }

        /* List side — search + tabs + card grid. */
        .hadacy-coupons-search {
          position: relative;
          margin-bottom: 0.9rem;
        }
        .hadacy-coupons-search__input {
          width: 100%;
          padding-left: 2.4rem;
        }
        .hadacy-coupons-search__icon {
          position: absolute;
          top: 50%; left: 0.9rem;
          transform: translateY(-50%);
          color: var(--muted);
          pointer-events: none;
          font-size: 0.85rem;
        }

        .hadacy-coupons-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          margin-bottom: 1rem;
        }
        .hadacy-coupons-tab {
          padding: 0.4rem 0.85rem;
          border-radius: 999px;
          background: transparent;
          border: 1px solid var(--hairline);
          color: var(--muted);
          font-size: 0.74rem;
          letter-spacing: 0.06em;
          cursor: pointer;
          font-family: "Jost", sans-serif;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .hadacy-coupons-tab:hover {
          color: var(--dark);
          border-color: var(--gold);
        }
        .hadacy-coupons-tab--active {
          background: var(--dark);
          color: var(--gold-light);
          border-color: var(--dark);
        }
        .hadacy-coupons-tab__count {
          margin-left: 0.4rem;
          font-size: 0.7rem;
          opacity: 0.85;
        }

        /* Coupon list cards. */
        .hadacy-coupons-list {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }
        .hadacy-coupon-card {
          background: #fff;
          border: 1px solid var(--hairline);
          border-radius: 14px;
          padding: 1.05rem 1.15rem;
          transition: border-color .2s ease, box-shadow .2s ease, transform .2s ease;
          opacity: 1;
        }
        .hadacy-coupon-card:hover {
          border-color: rgba(184, 147, 58, 0.45);
          box-shadow: 0 8px 24px rgba(26, 20, 16, 0.06);
        }
        .hadacy-coupon-card--deleting { opacity: 0.55; }
        .hadacy-coupon-card__head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 0.85rem;
          margin-bottom: 0.4rem;
        }
        .hadacy-coupon-card__code {
          font-family: "Cormorant Garamond", serif;
          font-size: 1.35rem;
          color: var(--dark);
          letter-spacing: 0.02em;
          margin: 0;
          word-break: break-word;
        }
        .hadacy-coupon-card__rule {
          font-size: 0.88rem;
          color: var(--muted);
          line-height: 1.55;
          margin: 0.25rem 0 0.55rem 0;
        }
        .hadacy-coupon-card__rule strong { color: var(--dark); font-weight: 500; }
        .hadacy-coupon-card__desc {
          font-size: 0.8rem;
          color: var(--muted);
          font-style: italic;
          margin: 0 0 0.65rem 0;
        }

        .hadacy-coupon-card__meta {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 0.4rem 0.9rem;
          padding: 0.6rem 0;
          margin-bottom: 0.65rem;
          border-top: 1px solid var(--hairline);
          border-bottom: 1px solid var(--hairline);
        }
        .hadacy-coupon-card__meta-row {
          display: flex;
          flex-direction: column;
          gap: 0.05rem;
        }
        .hadacy-coupon-card__meta-label {
          font-size: 0.58rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--muted);
        }
        .hadacy-coupon-card__meta-value {
          font-size: 0.82rem;
          color: var(--dark);
        }

        .hadacy-coupon-card__foot {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.6rem;
          flex-wrap: wrap;
        }
        .hadacy-coupon-card__actions {
          display: flex;
          gap: 0.5rem;
        }
        .hadacy-coupon-card__icon {
          width: 34px; height: 34px;
          border-radius: 999px;
          border: 1px solid var(--hairline);
          background: transparent;
          color: var(--muted);
          font-size: 0.82rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .hadacy-coupon-card__icon:hover {
          background: var(--recess);
          color: var(--dark);
          border-color: var(--gold);
        }
        .hadacy-coupon-card__icon--danger:hover {
          background: rgba(176, 74, 60, 0.1);
          color: var(--danger);
          border-color: var(--danger);
        }
        .hadacy-coupon-card__icon:disabled { opacity: 0.55; cursor: not-allowed; }

        /* Status pills */
        .hadacy-coupons-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.25rem 0.6rem;
          border-radius: 999px;
          font-size: 0.66rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          border: 1px solid;
          background: transparent;
          font-weight: 500;
        }
        .hadacy-coupons-pill::before {
          content: "";
          display: inline-block;
          width: 6px; height: 6px;
          border-radius: 999px;
          background: currentColor;
        }
        .hadacy-coupons-pill--ok {
          color: var(--success);
          border-color: rgba(91, 122, 60, 0.4);
          background: rgba(91, 122, 60, 0.08);
        }
        .hadacy-coupons-pill--info {
          color: var(--gold);
          border-color: rgba(184, 147, 58, 0.4);
          background: rgba(184, 147, 58, 0.08);
        }
        .hadacy-coupons-pill--muted {
          color: var(--muted);
          border-color: var(--hairline);
          background: var(--recess);
        }
        .hadacy-coupons-pill--warn {
          color: var(--danger);
          border-color: rgba(176, 74, 60, 0.4);
          background: rgba(176, 74, 60, 0.08);
        }
        .hadacy-coupons-pill--accumulable {
          color: var(--gold);
          border-color: rgba(184, 147, 58, 0.4);
          background: transparent;
        }
        .hadacy-coupons-pill--accumulable::before { display: none; }

        /* Empty state */
        .hadacy-coupons-empty {
          background: var(--recess);
          border: 1px dashed var(--hairline);
          border-radius: 14px;
          padding: 2rem 1.2rem;
          text-align: center;
        }
        .hadacy-coupons-empty__icon {
          font-size: 1.6rem;
          color: var(--gold);
          margin-bottom: 0.6rem;
        }
        .hadacy-coupons-empty__title {
          font-family: "Cormorant Garamond", serif;
          font-size: 1.3rem;
          color: var(--dark);
          margin: 0 0 0.3rem 0;
        }
        .hadacy-coupons-empty__body {
          font-size: 0.85rem;
          color: var(--muted);
          line-height: 1.55;
          margin: 0;
          max-width: 360px;
          margin-left: auto;
          margin-right: auto;
        }

        /* Banners (errors) */
        .hadacy-coupons-banner {
          padding: 0.7rem 0.95rem;
          border-radius: 10px;
          margin-bottom: 1rem;
          font-size: 0.86rem;
          line-height: 1.45;
          border: 1px solid;
          display: flex;
          align-items: flex-start;
          gap: 0.55rem;
        }
        .hadacy-coupons-banner--danger {
          background: rgba(176, 74, 60, 0.08);
          border-color: rgba(176, 74, 60, 0.32);
          color: var(--danger);
        }

        /* Toast (corner) */
        .hadacy-coupons-toast {
          position: fixed;
          right: 1.2rem;
          bottom: 1.2rem;
          z-index: 1080;
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-left: 4px solid var(--gold);
          border-radius: 10px;
          padding: 0.7rem 1rem;
          font-size: 0.88rem;
          color: var(--dark);
          box-shadow: 0 14px 40px rgba(26, 20, 16, 0.14);
          max-width: 320px;
          animation: hadacy-coupons-toast-in .25s ease both;
        }
        .hadacy-coupons-toast--ok { border-left-color: var(--success); }
        .hadacy-coupons-toast--warn { border-left-color: var(--gold); }
        .hadacy-coupons-toast--danger { border-left-color: var(--danger); }
        @keyframes hadacy-coupons-toast-in {
          from { transform: translateY(8px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      <header className="hadacy-coupons__header">
        <div className="hadacy-coupons__eyebrow">HADACY · administración</div>
        <h1 className="hadacy-coupons__title">Cupones &amp; promociones</h1>
        <p className="hadacy-coupons__lede">
          Crea descuentos para tus clientes, prográmalos en el tiempo y
          monitorea su estado en vivo. Los cambios se guardan al instante y se
          aplican en automático en el carrito.
        </p>
      </header>

      <section className="hadacy-coupons-help">
        <div className="hadacy-coupons-help__item">
          <span className="hadacy-coupons-help__icon">
            <i className="fas fa-percent"></i>
          </span>
          <div>
            <h3 className="hadacy-coupons-help__title">Porcentaje (%)</h3>
            <p className="hadacy-coupons-help__body">
              Resta un porcentaje fijo del subtotal del carrito. Ideal para
              campañas tipo <code>HADALOVE30</code> con un 30% off general.
            </p>
          </div>
        </div>
        <div className="hadacy-coupons-help__item">
          <span className="hadacy-coupons-help__icon">
            <i className="fas fa-gift"></i>
          </span>
          <div>
            <h3 className="hadacy-coupons-help__title">Compra X, llévate Y</h3>
            <p className="hadacy-coupons-help__body">
              Por cada <code>X</code> productos del mismo tipo (perfumes o
              cosméticos), se bonifican <code>Y</code> gratis. El descuento se
              aplica al producto más barato del grupo.
            </p>
          </div>
        </div>
      </section>

      <div className="hadacy-coupons__grid">
        {/* ── FORM PANEL ─────────────────────────────────────────────── */}
        <div className="hadacy-coupons-panel">
          <div className="hadacy-coupons-panel__head">
            <h2 className="hadacy-coupons-panel__title">
              {form._id ? "Editar cupón" : "Nuevo cupón"}
            </h2>
            <span className="hadacy-coupons-panel__hint">
              {form._id ? `ID: ${String(form._id).slice(-8).toUpperCase()}` : "Empezar de cero"}
            </span>
          </div>

          <form onSubmit={onSubmit} noValidate>
            {/* ── 1 · Identidad ───────────────────────────────────── */}
            <div className="hadacy-coupons-section">
              <h3 className="hadacy-coupons-section__title">
                <span>1 · Identidad</span>
              </h3>

              <div className="hadacy-coupons-field">
                <label htmlFor="cp-name" className="hadacy-coupons-label">
                  Código del cupón
                </label>
                <input
                  id="cp-name"
                  type="text"
                  className="hadacy-coupons-input"
                  placeholder="HADALOVE30"
                  value={form.Name}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      Name: e.target.value.toUpperCase(),
                    }))
                  }
                />
                <span className="hadacy-coupons-help-text">
                  Esto es lo que el cliente escribe en el carrito. Solo
                  mayúsculas, sin espacios. Ej: <code>HADALOVE30</code>,{" "}
                  <code>VERANO2X1</code>.
                </span>
              </div>

              <div className="hadacy-coupons-field">
                <label htmlFor="cp-desc" className="hadacy-coupons-label">
                  Descripción interna
                </label>
                <input
                  id="cp-desc"
                  type="text"
                  className="hadacy-coupons-input"
                  placeholder="Campaña Día de las Madres 2026"
                  value={form.Description}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, Description: e.target.value }))
                  }
                />
                <span className="hadacy-coupons-help-text">
                  Solo la ven los administradores. Sirve para recordar el
                  contexto del cupón.
                </span>
              </div>
            </div>

            {/* ── 2 · Regla ────────────────────────────────────────── */}
            <div className="hadacy-coupons-section">
              <h3 className="hadacy-coupons-section__title">
                <span>2 · Regla del descuento</span>
              </h3>

              <div className="hadacy-coupons-field">
                <label htmlFor="cp-type" className="hadacy-coupons-label">
                  Tipo de descuento
                </label>
                <select
                  id="cp-type"
                  className="hadacy-coupons-select"
                  value={form.Type}
                  onChange={(e) =>
                    setForm((f) => {
                      const nextType = e.target.value;
                      if (nextType === "BUY_X_GET_Y_SAME_TIPO") {
                        return {
                          ...f,
                          Type: nextType,
                          MatchField: "tipo",
                          MustMatchSameField: true,
                        };
                      }
                      return { ...f, Type: nextType };
                    })
                  }
                >
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {form.Type === "PERCENT" && (
                <div className="hadacy-coupons-field">
                  <label htmlFor="cp-discount" className="hadacy-coupons-label">
                    Descuento (%)
                  </label>
                  <input
                    id="cp-discount"
                    type="number"
                    min={0}
                    max={100}
                    className="hadacy-coupons-input"
                    value={form.Discount}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        Discount: Number(e.target.value),
                      }))
                    }
                  />
                  <span className="hadacy-coupons-help-text">
                    Porcentaje que se resta del subtotal. Usa <code>30</code>{" "}
                    para 30% off.
                  </span>
                </div>
              )}

              {form.Type === "BUY_X_GET_Y_SAME_TIPO" && (
                <>
                  <div className="hadacy-coupons-row">
                    <div className="hadacy-coupons-field">
                      <label
                        htmlFor="cp-threshold"
                        className="hadacy-coupons-label"
                      >
                        Compra X unidades
                      </label>
                      <input
                        id="cp-threshold"
                        type="number"
                        min={1}
                        className="hadacy-coupons-input"
                        value={form.ThresholdQty}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            ThresholdQty: Number(e.target.value),
                          }))
                        }
                      />
                      <span className="hadacy-coupons-help-text">
                        Cuántas piezas debe meter el cliente al carrito.
                      </span>
                    </div>
                    <div className="hadacy-coupons-field">
                      <label htmlFor="cp-free" className="hadacy-coupons-label">
                        Y se llevan Y gratis
                      </label>
                      <input
                        id="cp-free"
                        type="number"
                        min={1}
                        className="hadacy-coupons-input"
                        value={form.FreeQty}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            FreeQty: Number(e.target.value),
                          }))
                        }
                      />
                      <span className="hadacy-coupons-help-text">
                        Cuántas se bonifican (las más baratas del grupo).
                      </span>
                    </div>
                  </div>

                  <div className="hadacy-coupons-field">
                    <label
                      htmlFor="cp-applies"
                      className="hadacy-coupons-label"
                    >
                      ¿A qué tipo aplica?
                    </label>
                    <select
                      id="cp-applies"
                      className="hadacy-coupons-select"
                      value={form.AppliesToTipo}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          AppliesToTipo: e.target.value,
                        }))
                      }
                    >
                      {TIPO_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <span className="hadacy-coupons-help-text">
                      Si eliges "Cualquiera", el cliente puede mezclar perfumes
                      y cosméticos siempre que sean del mismo tipo entre sí.
                    </span>
                  </div>
                </>
              )}

              {renderFormRulePreview()}
            </div>

            {/* ── 3 · Vigencia & uso ───────────────────────────────── */}
            <div className="hadacy-coupons-section">
              <h3 className="hadacy-coupons-section__title">
                <span>3 · Vigencia &amp; uso</span>
              </h3>

              <div className="hadacy-coupons-row">
                <div className="hadacy-coupons-field">
                  <label htmlFor="cp-starts" className="hadacy-coupons-label">
                    Inicio (opcional)
                  </label>
                  <input
                    id="cp-starts"
                    type="datetime-local"
                    className="hadacy-coupons-input"
                    value={form.StartsAt}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, StartsAt: e.target.value }))
                    }
                  />
                </div>
                <div className="hadacy-coupons-field">
                  <label htmlFor="cp-ends" className="hadacy-coupons-label">
                    Fin (opcional)
                  </label>
                  <input
                    id="cp-ends"
                    type="datetime-local"
                    className="hadacy-coupons-input"
                    value={form.EndsAt}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, EndsAt: e.target.value }))
                    }
                  />
                </div>
              </div>
              <span className="hadacy-coupons-help-text">
                Si dejas estos campos vacíos, el cupón aplica desde que se crea
                hasta que lo desactives manualmente. Fuera de esta ventana el
                cupón no se acepta aunque esté activo.
              </span>

              <div
                className="hadacy-coupons-field"
                style={{ marginTop: "0.95rem" }}
              >
                <label className="hadacy-coupons-switch">
                  <input
                    type="checkbox"
                    checked={form.Active}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, Active: e.target.checked }))
                    }
                  />
                  <span className="hadacy-coupons-switch__toggle"></span>
                  <span className="hadacy-coupons-switch__copy">
                    <span className="hadacy-coupons-switch__title">
                      Cupón activo
                    </span>
                    <span className="hadacy-coupons-switch__desc">
                      Si está apagado, el cupón existe pero los clientes no
                      pueden canjearlo.
                    </span>
                  </span>
                </label>
              </div>

              <div className="hadacy-coupons-field">
                <label className="hadacy-coupons-switch">
                  <input
                    type="checkbox"
                    checked={form.Stackable}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, Stackable: e.target.checked }))
                    }
                  />
                  <span className="hadacy-coupons-switch__toggle"></span>
                  <span className="hadacy-coupons-switch__copy">
                    <span className="hadacy-coupons-switch__title">
                      Acumulable con otros cupones
                    </span>
                    <span className="hadacy-coupons-switch__desc">
                      Permite combinar este cupón con otros en el mismo
                      carrito. Por defecto los cupones son exclusivos.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="hadacy-coupons-actions">
              <button
                type="submit"
                className="hadacy-coupons-cta"
                disabled={saving}
              >
                {saving ? (
                  <>Guardando…</>
                ) : (
                  <>
                    {form._id ? "Actualizar cupón" : "Crear cupón"}
                    <i className="fas fa-arrow-right"></i>
                  </>
                )}
              </button>
              <button
                type="button"
                className="hadacy-coupons-cta hadacy-coupons-cta--ghost"
                onClick={resetForm}
                disabled={saving}
              >
                {form._id ? "Cancelar edición" : "Limpiar"}
              </button>
            </div>
          </form>
        </div>

        {/* ── LIST PANEL ─────────────────────────────────────────────── */}
        <div className="hadacy-coupons-panel">
          <div className="hadacy-coupons-panel__head">
            <h2 className="hadacy-coupons-panel__title">Cupones existentes</h2>
            <span className="hadacy-coupons-panel__hint">
              {statusCounts.all} {statusCounts.all === 1 ? "cupón" : "cupones"}
            </span>
          </div>

          <div className="hadacy-coupons-search">
            <i className="fas fa-search hadacy-coupons-search__icon"></i>
            <input
              type="text"
              className="hadacy-coupons-input hadacy-coupons-search__input"
              placeholder="Buscar por código, descripción o tipo…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>

          <div className="hadacy-coupons-tabs">
            {[
              { key: "all", label: "Todos" },
              { key: "active", label: "Activos" },
              { key: "scheduled", label: "Programados" },
              { key: "paused", label: "Pausados" },
              { key: "expired", label: "Expirados" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`hadacy-coupons-tab ${
                  statusFilter === tab.key ? "hadacy-coupons-tab--active" : ""
                }`}
                onClick={() => setStatusFilter(tab.key)}
              >
                {tab.label}
                <span className="hadacy-coupons-tab__count">
                  {statusCounts[tab.key]}
                </span>
              </button>
            ))}
          </div>

          {error && (
            <div className="hadacy-coupons-banner hadacy-coupons-banner--danger">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <Loader />
          ) : list.length === 0 ? (
            <div className="hadacy-coupons-empty">
              <div className="hadacy-coupons-empty__icon">
                <i className="fas fa-ticket-alt"></i>
              </div>
              <h3 className="hadacy-coupons-empty__title">
                {statusCounts.all === 0
                  ? "Aún no hay cupones"
                  : "Sin resultados"}
              </h3>
              <p className="hadacy-coupons-empty__body">
                {statusCounts.all === 0
                  ? "Crea tu primer cupón en el panel de la izquierda. Puedes empezar con un porcentaje fijo o un 2x1 por categoría."
                  : "Ajusta el buscador o cambia de pestaña para encontrar el cupón que buscas."}
              </p>
            </div>
          ) : (
            <div className="hadacy-coupons-list">
              {list.map((c) => {
                const status = couponStatus(c);
                const meta = STATUS_META[status];
                const isDeleting = deletingIds.has(c._id);
                return (
                  <article
                    key={c._id}
                    className={`hadacy-coupon-card ${
                      isDeleting ? "hadacy-coupon-card--deleting" : ""
                    }`}
                  >
                    <header className="hadacy-coupon-card__head">
                      <h3 className="hadacy-coupon-card__code">{c.Name}</h3>
                      <span
                        className={`hadacy-coupons-pill hadacy-coupons-pill--${meta.tone}`}
                      >
                        {meta.label}
                      </span>
                    </header>

                    <p className="hadacy-coupon-card__rule">
                      {renderRuleSummary(c)}
                    </p>

                    {c.Description && (
                      <p className="hadacy-coupon-card__desc">
                        {c.Description}
                      </p>
                    )}

                    <div className="hadacy-coupon-card__meta">
                      <div className="hadacy-coupon-card__meta-row">
                        <span className="hadacy-coupon-card__meta-label">
                          Inicio
                        </span>
                        <span className="hadacy-coupon-card__meta-value">
                          {fmtDT(c.StartsAt)}
                        </span>
                      </div>
                      <div className="hadacy-coupon-card__meta-row">
                        <span className="hadacy-coupon-card__meta-label">
                          Fin
                        </span>
                        <span className="hadacy-coupon-card__meta-value">
                          {fmtDT(c.EndsAt)}
                        </span>
                      </div>
                    </div>

                    <div className="hadacy-coupon-card__foot">
                      {c.Stackable ? (
                        <span className="hadacy-coupons-pill hadacy-coupons-pill--accumulable">
                          <i
                            className="fas fa-layer-group"
                            style={{ marginRight: "0.35rem" }}
                          ></i>
                          Acumulable
                        </span>
                      ) : (
                        <span style={{ flex: 1 }}></span>
                      )}
                      <div className="hadacy-coupon-card__actions">
                        <button
                          type="button"
                          className="hadacy-coupon-card__icon"
                          onClick={() => onEdit(c)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <i className="fas fa-pen"></i>
                        </button>
                        <button
                          type="button"
                          className="hadacy-coupon-card__icon hadacy-coupon-card__icon--danger"
                          onClick={() => onDelete(c)}
                          disabled={isDeleting}
                          aria-label="Eliminar"
                          title="Eliminar"
                        >
                          <i
                            className={
                              isDeleting
                                ? "fas fa-spinner fa-spin"
                                : "fas fa-trash"
                            }
                          ></i>
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {toast.show && (
        <div
          className={`hadacy-coupons-toast hadacy-coupons-toast--${toast.tone}`}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default CouponsAdminScreen;
